// Global Instructions Rule Applied!
//
// Correction flow (owner-approved, mockup v4): the reader spots stale/wrong
// agent content and corrects it in place. The correction lands in the agent's
// MEMORY (importance-5 owner statement) and the brief re-materializes — the
// brief is derived state, so brief and memory never diverge. One modal, no
// workflow, nothing assigned to anyone.
import React, { useState } from 'react'
import { Modal, Input } from 'antd'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { correctCompanyBrief } from '../services/internalAgentsService.ts'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'

interface BriefCorrectionModalProps {
  open: boolean
  accountId: string
  sectionKey: string | null
  sectionLabel: string | null
  onClose: () => void
  // Parent refetches the brief (regeneration already happened server-side).
  onCorrected: () => void
}

export const BriefCorrectionModal: React.FC<BriefCorrectionModalProps> = ({
  open,
  accountId,
  sectionKey,
  sectionLabel,
  onClose,
  onCorrected
}) => {
  const [correction, setCorrection] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const token = () => getAccessToken(store.getState().auth)

  const handleOk = async () => {
    if (!sectionKey || !correction.trim()) return
    setSubmitting(true)
    try {
      await correctCompanyBrief(
        { account_id: accountId, section_key: sectionKey, correction: correction.trim() },
        token()
      )
      toast.success('Correction recorded — brief regenerated')
      setCorrection('')
      onCorrected()
      onClose()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Correction failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title={`Correct “${sectionLabel ?? sectionKey ?? ''}”`}
      open={open}
      onOk={handleOk}
      okText='Submit correction'
      okButtonProps={{ disabled: !correction.trim() }}
      confirmLoading={submitting}
      onCancel={() => {
        setCorrection('')
        onClose()
      }}
      destroyOnClose
    >
      <p className='text-gray-600'>
        Your correction is recorded in the agent&apos;s memory as an owner statement
        (importance 5) and the brief re-generates from memory — the text below
        won&apos;t be pasted verbatim.
      </p>
      <Input.TextArea
        rows={4}
        value={correction}
        onChange={(e) => setCorrection(e.target.value)}
        placeholder='What is stale or incorrect, and what is actually true?'
      />
    </Modal>
  )
}
